import useRolePermission from "entities/auth/hooks/useRolePermission";
import { useDeleteFaqGroupMutation } from "entities/faqs/mutations/useDeleteFaqGroupMutation";
import { useReorderFaqGroupsMutation } from "entities/faqs/mutations/useReorderFaqGroupsMutation";
import { useUpdateFaqGroupStatusMutation } from "entities/faqs/mutations/useUpdateFaqGroupStatusMutation";
import { useFaqGroupsQuery } from "entities/faqs/queries/useFaqGroupsQuery";
import { FaqGroup } from "entities/faqs/types";
import { FC, PropsWithChildren } from "react";
import { useLocation } from "react-router-dom";
import { useDrawerState } from "shared/hooks/useDrawerState";
import { useModalState } from "shared/hooks/useModalState";
import { createContext } from "shared/utils/context";
import { FaqGroupsColumnsProps } from "../types";
import { FaqGroupPageType } from "./types";

const [Provider, useFaqGroupPage] = createContext<FaqGroupPageType>({
  name: "FaqGroupPageContext",
  hookName: "useFaqGroupPage",
  providerName: "FaqGroupPageProvider",
});

const FaqGroupPageProvider: FC<PropsWithChildren> = ({ children }) => {
  const faqGroupDrawer = useDrawerState<string>();
  const deleteModal = useModalState();
  const { pathname: currentGroupPath } = useLocation();

  const { data: faqGroups = [], isLoading } = useFaqGroupsQuery();
  const { mutate: mutateReorderFaqGroups, isPending } =
    useReorderFaqGroupsMutation();

  const { mutate: deleteFaqGroup, isPending: isPendingDeleteFaqGroup } =
    useDeleteFaqGroupMutation(deleteModal?.modalDetails?.id);
  const { mutate: updateFaqGroupStatus, isPending: isFaqGroupStatusChanging } =
    useUpdateFaqGroupStatusMutation();

  const handleOpenDeleteModal = (faqGroup: FaqGroup) => {
    deleteModal.openModal(faqGroup);
  };

  const handleDeleteFaqGroup = () => {
    deleteFaqGroup({
      faqGroupId: deleteModal.modalDetails?.id,
      faqGroups,
    });
    deleteModal.closeModal();
  };

  const handleActiveStatusPage = (faqGroup: FaqGroup) => {
    updateFaqGroupStatus({ faqGroupId: faqGroup.id, status: faqGroup.status });
  };

  const writeAccess = useRolePermission("faq:write");
  const columnsParams: FaqGroupsColumnsProps = {
    currentLocation: currentGroupPath,
    handlePublishStatus: handleActiveStatusPage,
    handleOpenDeleteModal,
    faqGroupDrawer,
    readOnly: !writeAccess.allowed,
  };

  const isLoadingFaqGroups =
    isLoading || isFaqGroupStatusChanging || isPendingDeleteFaqGroup;

  const value = {
    data: faqGroups,
    faqGroupDrawer,
    columnsParams,
    isLoadingFaqGroups,
    handleDeleteFaqGroup,
    mutateReorderFaqGroups,
    deleteModal,
    isLoadingDeleteFaqGroup: isPendingDeleteFaqGroup,
    isLoadingReorderList: isPending,
  };

  return <Provider value={value}>{children}</Provider>;
};

export { FaqGroupPageProvider, useFaqGroupPage };
